import React, { useState, useEffect } from 'react';
import styles from './PreferencesModal.module.css';
import axios from '../api/axios';

interface PositionOption {
  id: number;
  title: string;
  department: string;
  description: string;
}

// API returns PascalCase, so we need this interface too
interface PositionApiResponse {
  Id: number;
  Title: string;
  Department: string;
  Description: string;
}

interface Preferences {
  interests: string[];
  playstyle: string;
  squadPreference: string;
  skills: string;
  primaryRole: string;
  secondaryRole: string;
  tertiaryRole: string;
}

interface Props {
  initial: Preferences;
  onSave: (updated: Preferences) => void;
  onClose: () => void;
}

const INTEREST_OPTIONS = [
  'Exploration', 'Combat', 'Mining', 'Trade', 'Salvage', 'Medical',
  'Leadership', 'RP / Lore', 'Logistics', 'Base Building', 'Hauling', 'Economy'
];

const PLAYSTYLE_OPTIONS = ['Casual', 'Balanced', 'Weekly Ops', 'Hardcore', 'undecided'];
const SQUAD_OPTIONS = ['Looking for a Squad', 'Wants to Lead a squad', 'Flight Squad', 'Ground Squad', 'Vehicle Squad'];

const PreferencesModal: React.FC<Props> = ({ initial, onSave, onClose }) => {
  const [prefs, setPrefs] = useState<Preferences>(initial);
  const [availableRoles, setAvailableRoles] = useState<PositionOption[]>([]);

  useEffect(() => {
    console.log('Fetching entry-level positions...');
    axios.get<PositionOption[]>('/positions/entry-level')
      .then(res => {
        console.log('Entry-level positions response:', res.data);
        // API already returns camelCase, use directly
        setAvailableRoles(res.data);
      })
      .catch(err => {
        console.error('Failed to load entry-level roles:', err);
        console.error('Error details:', err.response?.data, err.response?.status);
      });
  }, []);

  const toggleInterest = (interest: string) => {
    setPrefs((prev) => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter((i) => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const getFilteredRoles = (exclude: string[]) =>
    availableRoles.filter(role => !exclude.includes(role.title));

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h2 className={styles.modalHeader}>Edit Org Journey</h2>
  
        <div className={styles.modalGrid}>
          <div>
            <label>Primary Interests</label>
            <div className={styles.optionsGrid}>
              {INTEREST_OPTIONS.map((interest) => (
                <label key={interest} className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={prefs.interests.includes(interest)}
                    onChange={() => toggleInterest(interest)}
                  />
                  {interest}
                </label>
              ))}
            </div>
          </div>
  
          <div>
            <label>Playstyle</label>
            <select
              value={prefs.playstyle}
              onChange={(e) => setPrefs({ ...prefs, playstyle: e.target.value })}
            >
              <option value="">Select...</option>
              {PLAYSTYLE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
  
          <div>
            <label>Squad Preference</label>
            <select
              value={prefs.squadPreference}
              onChange={(e) => setPrefs({ ...prefs, squadPreference: e.target.value })}
            >
              <option value="">Select...</option>
              {SQUAD_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
  
          <div>
            <label>Skills / Contributions</label>
            <textarea
              value={prefs.skills}
              onChange={(e) => setPrefs({ ...prefs, skills: e.target.value })}
              rows={4}
            />
          </div>
  
          {/* 💡 Role Preferences Section (Prominent) */}
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '0.5rem', color: '#222' }}>
              What Position(s) are you interested in?
            </h3>
            <p style={{ fontSize: '14px', color: '#555', marginBottom: '1rem' }}>
              Select the roles you're most interested in filling within the org. This helps leadership
              match you with future activities, departments, or teams. As expected, Primary is considered to be your #1 choice above the others.
            </p>
          </div>
  
          <div className={styles.roleBlock}>
            <div>
              <label>Primary Position</label>
              <select
                value={prefs.primaryRole || ''}
                onChange={(e) => setPrefs({ ...prefs, primaryRole: e.target.value })}
              >
                <option value="">Select...</option>
                {getFilteredRoles([prefs.secondaryRole || '', prefs.tertiaryRole || '']).map(role => (
                  <option
                    key={role.id}
                    value={role.title}
                    title={role.description || 'No description provided.'}
                  >
                    {role.title}{role.department ? ` (${role.department})` : ''}
                  </option>
                ))}
              </select>
            </div>
  
            <div>
              <label>Secondary Position</label>
              <select
                value={prefs.secondaryRole || ''}
                onChange={(e) => setPrefs({ ...prefs, secondaryRole: e.target.value })}
              >
                <option value="">Select...</option>
                {getFilteredRoles([prefs.primaryRole || '', prefs.tertiaryRole || '']).map(role => (
                  <option
                    key={role.id}
                    value={role.title}
                    title={role.description || 'No description provided.'}
                  >
                    {role.title}{role.department ? ` (${role.department})` : ''}
                  </option>
                ))}
              </select>
            </div>
  
            <div>
              <label>Tertiary Position</label>
              <select
                value={prefs.tertiaryRole || ''}
                onChange={(e) => setPrefs({ ...prefs, tertiaryRole: e.target.value })}
              >
                <option value="">Select...</option>
                {getFilteredRoles([prefs.primaryRole || '', prefs.secondaryRole || '']).map(role => (
                  <option
                    key={role.id}
                    value={role.title}
                    title={role.description || 'No description provided.'}
                  >
                    {role.title}{role.department ? ` (${role.department})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
  
        <div className={styles.modalButtons}>
          <button onClick={() => onSave(prefs)}>Save</button>
          <button className={styles.cancelButton} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
  
};

export default PreferencesModal;
